<template>
  <div class="col-xl-12">
    <div class="card stock-dashboard-card" ref="stockCard">
      <!-- HEADER -->
      <div class="card-header card-no-border">
        <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <h5>DEALER BUFFER STOCK AVAILABILITY</h5>

          <div class="d-flex align-items-center gap-2">
            <div
              class="refresh-icon"
              @click="handleRefresh"
              :class="{ disabled: loading }"
              title="Refresh"
            >
              <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-sync'"></i>
            </div>

            <div class="icon-btn" @click="toggleFullScreen" title="Fullscreen">
              <i :class="isFullScreen ? 'fa fa-minimize' : 'fa fa-maximize'"></i>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-start align-items-start flex-wrap gap-2">
          <div class="header-top">
            <div class="d-flex gap-2 flex-wrap">

              <input
                type="date"
                v-model="selectedDate"
                class="form-control"
                style="width: 140px"
                :max="todayDate"
                @change="fetchStockData"
              />

              <input
                type="text"
                v-model="dealerSearch"
                class="form-control"
                placeholder="Search dealer..."
                style="width: 180px"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- BODY -->
      <div class="card-body pt-0">
        <div v-if="loading" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <div v-else-if="error" class="error-container">
          <p class="error-message">{{ error }}</p>
          <button class="stock-retry-btn" @click="fetchStockData" :disabled="loading">
            Retry
          </button>
        </div>

        <div v-else-if="!rows.length || !items.length" class="empty-container">
          <p class="empty-message">No buffer stock data available for the selected filters.</p>
        </div>

        <div v-else-if="!filteredRows.length" class="empty-container">
          <p class="empty-message">No matching dealer found.</p>
        </div>

        <div v-else class="stock-table-wrap" :style="{ maxHeight: tableMaxHeight + 'px' }">
          <table class="stock-table">
            <thead>
              <!-- Item group row -->
              <tr>
                <th class="dealer-col sticky-col" rowspan="3">Dealer</th>
                <th class="se-col sticky-col-2" rowspan="3">Sales Executive</th>
                <th class="route-col sticky-col-3" rowspan="3">Route</th>

                <th
                  v-for="item in items"
                  :key="item.filled_item_id"
                  class="item-group-col"
                  colspan="6"
                >
                  {{ item.item_name }}
                </th>
              </tr>

              <!-- Full / Empty sub-group row -->
              <tr>
                <template v-for="item in items" :key="item.filled_item_id + '-fe'">
                  <th class="full-group-col" colspan="3">Full</th>
                  <th class="empty-group-col" colspan="3">Empty</th>
                </template>
              </tr>

              <!-- Req / Ava row -->
              <tr>
                <template v-for="item in items" :key="item.filled_item_id + '-ra'">
                  <th class="sub-col req-col">Req</th>
                  <th class="sub-col ava-col">Ava</th>
                  <th class="sub-col bal-col">Bal</th>
                  <th class="sub-col req-col empty-boundary">Req</th>
                  <th class="sub-col ava-col">Ava</th>
                  <th class="sub-col bal-col">Bal</th>
                </template>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in filteredRows" :key="row.dealer_profile_id">
                <td class="dealer-col sticky-col">{{ row.dealer_name }}</td>
                <td class="se-col sticky-col-2">{{ row.se_name }}</td>
                <td class="route-col sticky-col-3">{{ row.route_name }}</td>

                <template v-for="cell in row.stock" :key="cell.item_id">
                  <td class="qty-cell req-col" :class="{ zero: !cell.full.req }">
                    {{ formatQty(cell.full.req) }}
                  </td>
                  <td
                    class="qty-cell ava-col"
                    :class="{ zero: !cell.full.ava, short: isShort(cell.full) }"
                  >
                    {{ formatQty(cell.full.ava) }}
                  </td>
                  <td
                    class="qty-cell bal-col"
                    :class="{ zero: !balance(cell.full), shortage: balance(cell.full) > 0 }"
                  >
                    {{ formatQty(balance(cell.full)) }}
                  </td>
                  <td class="qty-cell req-col empty-boundary" :class="{ zero: !cell.empty.req }">
                    {{ formatQty(cell.empty.req) }}
                  </td>
                  <td
                    class="qty-cell ava-col"
                    :class="{ zero: !cell.empty.ava, short: isShort(cell.empty) }"
                  >
                    {{ formatQty(cell.empty.ava) }}
                  </td>
                  <td
                    class="qty-cell bal-col"
                    :class="{ zero: !balance(cell.empty), shortage: balance(cell.empty) > 0 }"
                  >
                    {{ formatQty(balance(cell.empty)) }}
                  </td>
                </template>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td class="dealer-col sticky-col">Total</td>
                <td class="se-col sticky-col-2"></td>
                <td class="route-col sticky-col-3"></td>

                <template v-for="item in items" :key="item.filled_item_id + '-footer'">
                  <td class="qty-cell req-col footer-cell">
                    {{ formatQty(columnTotal(item.filled_item_id, "full", "req")) }}
                  </td>
                  <td class="qty-cell ava-col footer-cell">
                    {{ formatQty(columnTotal(item.filled_item_id, "full", "ava")) }}
                  </td>
                  <td class="qty-cell bal-col footer-cell">
                    {{ formatQty(columnBalanceTotal(item.filled_item_id, "full")) }}
                  </td>
                  <td class="qty-cell req-col footer-cell empty-boundary">
                    {{ formatQty(columnTotal(item.filled_item_id, "empty", "req")) }}
                  </td>
                  <td class="qty-cell ava-col footer-cell">
                    {{ formatQty(columnTotal(item.filled_item_id, "empty", "ava")) }}
                  </td>
                  <td class="qty-cell bal-col footer-cell">
                    {{ formatQty(columnBalanceTotal(item.filled_item_id, "empty")) }}
                  </td>
                </template>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchDistributors,
  fetchDealerBufferStock,
} from "@/services/dataService";
import { getApiErrorMessage } from "@/utils/apiError";

export default {
  data() {
    const userLabel = localStorage.getItem("user_label");
    return {
      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      reportingProfileId: localStorage.getItem("reportingL1ProfileId") || "",
      isDistributor: userLabel === "DISTRIBUTOR",
      isDistributorBackOffice: userLabel === "DISTRIBUTOR BACK OFFICE",

      loading: false,
      error: null,
      isFullScreen: false,
      tableMaxHeight: 360,

      distributors: [],
      selectedDistributor: "",
      selectedDate: new Date().toISOString().split("T")[0],

      dealerSearch: "",

      items: [],
      rows: [],

      distributorsLoaded: false,
      distributorRequestPromise: null,
      statsRequestSequence: 0,
      componentActive: true,
    };
  },

  mounted() {
    if (this.isDistributor) {
      this.selectedDistributor = this.profileId;
      this.lazyLoadDistributors();
    } else if (this.isDistributorBackOffice) {
      this.selectedDistributor = this.reportingProfileId;
      this.lazyLoadDistributors();
    }

    this.tableMaxHeight = this.isFullScreen
      ? window.innerHeight - 160
      : 360;

    this.fetchStockData();

    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
  },

  beforeUnmount() {
    this.componentActive = false;
    this.statsRequestSequence += 1;

    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
  },

  computed: {
    todayDate() {
      return new Date().toISOString().split("T")[0];
    },

    filteredRows() {
      const search = this.dealerSearch.trim().toLowerCase();

      if (!search) {
        return this.rows;
      }

      return this.rows.filter((row) =>
        (row.dealer_name || "").toLowerCase().includes(search)
      );
    },
  },

  methods: {
    handleFullscreenChange() {
      this.isFullScreen = !!document.fullscreenElement;

      this.$nextTick(() => {
        this.tableMaxHeight = this.isFullScreen
          ? window.innerHeight - 160
          : 360;
      });
    },

    async lazyLoadDistributors() {
      if (this.distributorsLoaded) {
        return;
      }

      if (this.distributorRequestPromise) {
        await this.distributorRequestPromise;
        return;
      }

      this.distributorRequestPromise = this.fetchDistributorList();

      try {
        const loaded = await this.distributorRequestPromise;

        if (loaded) {
          this.distributorsLoaded = true;
        }
      } finally {
        this.distributorRequestPromise = null;
      }
    },

    async fetchDistributorList() {
      try {
        const response = await fetchDistributors(this.profileId, this.token);

        if (!this.componentActive) {
          return false;
        }

        this.distributors = Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        return true;
      } catch (error) {
        console.error(
          "Distributor list API failed:",
          error?.response?.status,
          error?.response?.data || error
        );

        return false;
      }
    },

    handleRefresh() {
      if (this.loading) {
        return;
      }

      this.fetchStockData();
    },

    async fetchStockData() {
      const requestId = ++this.statsRequestSequence;

      try {
        this.loading = true;
        this.error = null;

        const payload = {
          profile_id: this.selectedDistributor || this.profileId,
          date: this.selectedDate,
        };

        const response = await fetchDealerBufferStock(payload, this.token);

        if (!this.componentActive || requestId !== this.statsRequestSequence) {
          return;
        }

        if (response.data.success) {
          this.items = response.data.items || [];
          this.rows = response.data.data || [];
        } else {
          this.items = [];
          this.rows = [];
          this.error = response.data.message || "No data available";
        }
      } catch (error) {
        if (!this.componentActive || requestId !== this.statsRequestSequence) {
          return;
        }

        console.error(
          "Dealer buffer stock API failed:",
          error?.response?.status,
          error?.response?.data || error
        );

        this.error = getApiErrorMessage(error);
      } finally {
        if (this.componentActive && requestId === this.statsRequestSequence) {
          this.loading = false;
        }
      }
    },

    isShort(cell) {
      return Number(cell?.ava || 0) < Number(cell?.req || 0);
    },

    balance(cell) {
      return Number(cell?.req || 0) - Number(cell?.ava || 0);
    },

    columnTotal(itemId, group, field) {
      return this.filteredRows.reduce((sum, row) => {
        const cell = (row.stock || []).find((c) => c.item_id === itemId);
        return sum + Number(cell?.[group]?.[field] || 0);
      }, 0);
    },

    columnBalanceTotal(itemId, group) {
      return (
        this.columnTotal(itemId, group, "req") -
        this.columnTotal(itemId, group, "ava")
      );
    },

    formatQty(value) {
      const num = Number(value || 0);
      return num % 1 === 0
        ? num.toLocaleString()
        : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },

    toggleFullScreen() {
      const element = this.$refs.stockCard;

      if (!document.fullscreenElement) {
        element.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    },
  },
};
</script>

<style scoped>
.stock-dashboard-card {
  height: 100%;
  border: 1px solid #e8edf4;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.stock-dashboard-card .card-header {
  padding: 10px 14px 8px !important;
  border-bottom: 1px solid #eef2f7;
  background: #ffffff;
}

.stock-dashboard-card .card-header h5 {
  margin: 0;
  color: #0f172a;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: -0.01em;
}

.stock-dashboard-card .card-body {
  padding: 8px 14px 12px !important;
}

.header-top {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

select {
  width: auto;
  font-size: 15px;
}

select option {
  font-size: 15px;
}

.refresh-icon,
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
  border: none;
  background: transparent;
  padding: 0;
}

.refresh-icon:hover,
.icon-btn:hover {
  background: #f1f3f5;
  color: #000;
}

.refresh-icon.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.spinner-container {
  display: flex;
  justify-content: center;
  height: 200px;
  align-items: center;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #6c5ce7;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.error-container,
.empty-container {
  text-align: center;
  padding: 30px 10px;
}

.error-message {
  color: red;
  font-weight: bold;
}

.empty-message {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.stock-retry-btn {
  min-height: 30px;
  margin-top: 8px;
  padding: 5px 12px;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.stock-retry-btn:hover:not(:disabled) {
  border-color: #7064f5;
  color: #7064f5;
  background: #f8f7ff;
}

.stock-retry-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Table */
.stock-table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  border: 1px solid #e8edf4;
  border-radius: 10px;
}

.stock-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.stock-table th,
.stock-table td {
  box-sizing: border-box;
}

.stock-table thead th {
  position: sticky;
  z-index: 2;
  background: #f8fafc;
  color: #334155;
  font-weight: 800;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  height: 30px;
  padding: 0 8px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  text-align: center;
  vertical-align: middle;
}

.stock-table thead tr:nth-child(1) th {
  top: 0;
}

.stock-table thead tr:nth-child(2) th {
  top: 30px;
}

.stock-table thead tr:nth-child(3) th {
  top: 60px;
}

.stock-table thead th.dealer-col {
  text-align: left;
}

.item-group-col {
  background: #f1edff;
  color: #6c5ce7;
  border-left: 2px solid #d8ccff;
}

.full-group-col {
  background: #eef7f0;
  color: #2e8b57;
  border-left: 1px solid #e2e8f0;
}

.empty-group-col {
  background: #fdf2e9;
  color: #c2790f;
  border-left: 1px solid #e2e8f0;
}

.sub-col {
  font-size: 10px;
  font-weight: 700;
  min-width: 58px;
}

.sub-col.req-col {
  border-left: 1px solid #e2e8f0;
}

.empty-boundary {
  border-left: 2px solid #e2e8f0 !important;
}

.stock-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

.dealer-col {
  text-align: left;
  font-weight: 700;
  color: #0f172a;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stock-table thead th.sticky-col {
  z-index: 4;
  background: #f8fafc;
}

.se-col {
  width: 160px;
  min-width: 160px;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
  color: #475569;
  border-left: 1px solid #e2e8f0;
}

.sticky-col-2 {
  position: sticky;
  left: 180px;
  z-index: 1;
  background: #ffffff;
}

.stock-table thead th.sticky-col-2 {
  z-index: 4;
  background: #f8fafc;
}

.stock-table tfoot td.sticky-col-2 {
  z-index: 5;
  background: #f8fafc;
}

.route-col {
  width: 140px;
  min-width: 140px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
  color: #475569;
  border-left: 1px solid #e2e8f0;
}

.sticky-col-3 {
  position: sticky;
  left: 340px;
  z-index: 1;
  background: #ffffff;
  box-shadow: 2px 0 0 0 #f1f5f9;
}

.stock-table thead th.sticky-col-3 {
  z-index: 4;
  background: #f8fafc;
}

.stock-table tfoot td.sticky-col-3 {
  z-index: 5;
  background: #f8fafc;
}

.qty-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #1f2937;
}

.qty-cell.req-col {
  border-left: 1px solid #f1f5f9;
}

.qty-cell.bal-col {
  border-left: 1px solid #f1f5f9;
  font-weight: 700;
}

.qty-cell.zero {
  color: #cbd5e1;
}

.qty-cell.short {
  color: #ef4444;
  font-weight: 700;
}

.qty-cell.shortage {
  color: #ef4444;
}

.footer-cell {
  font-weight: 800;
  background: #f8fafc;
  color: #0f172a;
}

.stock-table tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 3;
}

tfoot tr td.sticky-col {
  z-index: 5;
  background: #f8fafc;
}
</style>